import { ChevronRight, Home } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

export function Breadcrumbs() {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  // Map route segments to readable names
  const getLabel = (segment: string): string => {
    const labelMap: Record<string, string> = {
      'app': 'Home',
      'practitioner': 'Practitioner',
      'admin': 'Admin',
      'profile': 'Profile',
      'preferences': 'Preferences',
      'category': 'Category',
      'sessions': 'Sessions',
      'clients': 'Clients',
      'credits': 'Credits & Billing',
      'perks': 'Perks',
      'subscription': 'Subscription',
      'referrals': 'Referrals',
      'analytics': 'Analytics',
      'notifications': 'Notifications',
      'all-sessions': 'All Sessions',
      'links': 'Session Links',
      'shorts': 'Social Shorts',
      'business': 'Business Intelligence',
      'billing': 'Billing',
      'practitioners': 'Practitioners',
      'users': 'Users',
      'system': 'System Health',
      'content': 'Content Management',
      'settings': 'Settings',
      'groups': 'Groups',
      'new': 'New',
      'history': 'History',
    };

    return labelMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
  };

  const breadcrumbs: BreadcrumbItem[] = pathnames.map((segment, index) => {
    const href = `/${pathnames.slice(0, index + 1).join('/')}`;
    return {
      label: getLabel(segment),
      href: index === pathnames.length - 1 ? undefined : href,
    };
  });

  // Don't show breadcrumbs on home page
  if (pathnames.length === 0 || (pathnames.length === 1 && pathnames[0] === 'app')) {
    return null;
  }

  return (
    <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-4">
      <Link
        to="/app"
        className="flex items-center hover:text-foreground transition-colors"
      >
        <Home className="h-4 w-4" />
      </Link>

      {breadcrumbs.map((breadcrumb, index) => (
        <div key={index} className="flex items-center space-x-2">
          <ChevronRight className="h-4 w-4" />
          {breadcrumb.href ? (
            <Link
              to={breadcrumb.href}
              className="hover:text-foreground transition-colors"
            >
              {breadcrumb.label}
            </Link>
          ) : (
            <span className="font-medium text-foreground">{breadcrumb.label}</span>
          )}
        </div>
      ))}
    </nav>
  );
}
